import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import {
  ComposedModal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@carbon/react";
import TextareaField from "../../../components/carbon/Form/TextareaField";

interface RejectFormFields {
  reason: string;
}

export interface RejectSupplierOcModalProps {
  open: boolean;
  /** The OC code, shown in the title. */
  code: string;
  isPending: boolean;
  onClose: () => void;
  /** Called with the trimmed, non-blank reason. */
  onSubmit: (reason: string) => void;
}

/**
 * The seller's reject dialog. The backend requires a reason when rejecting, so the submit stays
 * disabled until a non-blank one is typed (and `onSubmit` never fires for a blank one).
 */
const RejectSupplierOcModal = ({
  open,
  code,
  isPending,
  onClose,
  onSubmit,
}: RejectSupplierOcModalProps) => {
  const { t } = useTranslation();
  const { control, handleSubmit, reset, watch } = useForm<RejectFormFields>({
    defaultValues: { reason: "" },
  });

  // Every open starts from an empty form.
  useEffect(() => {
    if (open) reset({ reason: "" });
  }, [open, reset]);

  const reasonBlank = watch("reason").trim() === "";

  const submit = (values: RejectFormFields) => {
    const reason = values.reason.trim();
    if (reason === "") return;
    onSubmit(reason);
  };

  const title = t("purchaseOrders.supplier.reject.title", { code });

  return (
    <ComposedModal open={open} size="md" aria-label={title} onClose={onClose}>
      <ModalHeader title={title} />
      <ModalBody>
        <form
          id="reject-supplier-oc-form"
          onSubmit={handleSubmit(submit)}
          noValidate
        >
          <TextareaField
            name="reason"
            label={t("purchaseOrders.supplier.reject.reason")}
            control={control}
            rows={3}
          />
        </form>
      </ModalBody>
      <ModalFooter
        secondaryButtonText={t("cta.cancel")}
        primaryButtonText={t("purchaseOrders.supplier.reject.submit")}
        danger
        loadingStatus={isPending ? "active" : "inactive"}
        loadingDescription={t("cta.loading")}
        primaryButtonDisabled={isPending || reasonBlank}
        onRequestSubmit={handleSubmit(submit)}
      >
        {null}
      </ModalFooter>
    </ComposedModal>
  );
};

export default RejectSupplierOcModal;
